'use client'

import { useCallback, useState } from 'react'
import {
	getGetMeQueryKey,
	useUpdateMe,
} from '@cocoonspace/sdk-js/domains/profile/profile'
import { cocoonPrivateRoutes } from '@cocoonspace/shared/config/cocoon-private-routes'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from '@cocoonspace/ui/components/dropdown-menu'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { useQueryClient } from '@tanstack/react-query'
import { signOut } from 'firebase/auth'
import {
	CalendarIcon,
	ChevronDownIcon,
	HelpCircleIcon,
	LogOutIcon,
} from 'lucide-react'
import { HomeIcon } from 'lucide-react'
import { SearchIcon } from 'lucide-react'
import { LinkIcon } from 'lucide-react'
import { UserRoundIcon } from 'lucide-react'
import { LanguagesIcon } from 'lucide-react'
import { MenuIcon } from 'lucide-react'
import Link from 'next/link'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useAnalytics } from 'use-analytics'

import { useLocale } from '~/hooks/use-locale.hook'
import { getLocalizedPath, stripLocaleFromPath } from '~/i18n/routing'
import { getAuthInstance, useAuth } from '~/providers/auth-provider'
import { useUserContext } from '~/providers/user-provider'
import { formatPersonName } from '~/utils/format-person-name'
import { UserAvatar } from '../user-avatar'

export const LangSelector = ({ dense = false }: { dense?: boolean }) => {
	const queryClient = useQueryClient()
	const { isAuth } = useAuth()
	const router = useRouter()
	const pathname = usePathname() ?? ''
	const searchParams = useSearchParams()
	const locale = useLocale()
	const { mutateAsync: updateMe } = useUpdateMe()

	const onUpdateLocale = useCallback(
		(value: string) => {
			if (locale === value) return

			const currentPath = stripLocaleFromPath(pathname)
			const search = searchParams.size ? `?${searchParams}` : ''

			router.push(
				getLocalizedPath(`${currentPath}${search}`, value as typeof locale),
			)

			if (isAuth) {
				updateMe({ data: { locale: value } }).then(() => {
					queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() })
				})
			}
		},
		[locale, pathname, searchParams, router, updateMe, queryClient, isAuth],
	)

	return (
		<Select
			onValueChange={onUpdateLocale}
			value={locale}
		>
			<SelectTrigger className='hover:bg-muted/10 flex w-full items-center justify-start rounded-sm border-none px-2 md:h-8'>
				{!dense && <LanguagesIcon />}
				<SelectValue className='flex w-full flex-1 items-center justify-between' />
			</SelectTrigger>

			<SelectContent>
				<SelectItem value='fr'>
					<span className='mr-2'>🇫🇷</span>
					{!dense && <span className='text-sm'>Français</span>}
				</SelectItem>

				<SelectItem value='en'>
					<span className='mr-2'>🇬🇧</span>
					{!dense && <span className='text-sm'>English</span>}
				</SelectItem>
			</SelectContent>
		</Select>
	)
}

const AuthMenuContent = ({ onSignOut }: { onSignOut?: () => void }) => {
	const { t } = useLingui()
	const router = useRouter()
	const pathname = stripLocaleFromPath(usePathname() ?? '')
	const analytics = useAnalytics()

	const handleSignOut = () => {
		const auth = getAuthInstance()

		return signOut(auth).then(() => {
			const mustRedirect = cocoonPrivateRoutes.find(
				({ path, mode }) => path.test(pathname) && mode === 'redirect',
			)

			analytics.reset()
			onSignOut?.()

			if (mustRedirect) {
				router.push('/')
			}
		})
	}

	return (
		<>
			<DropdownMenuItem asChild>
				<Link href='/dashboard'>
					<HomeIcon />
					{t`Accueil`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href='/paris'>
					<SearchIcon />
					{t`Trouver un Cocoon`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href='/events'>
					<CalendarIcon />
					{t`Mes événements`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href='/scheduling'>
					<LinkIcon />
					{t`Prise de rendez-vous`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuItem asChild>
				<Link href='/account'>
					<UserRoundIcon />
					{t`Mon compte`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuSeparator className='bg-border' />

			<DropdownMenuItem asChild>
				<Link
					href='https://support.cocoon-space.com'
					target='_blank'
				>
					<HelpCircleIcon />
					{t`Besoin d'aide ?`}
				</Link>
			</DropdownMenuItem>

			<DropdownMenuSeparator className='bg-border' />

			<DropdownMenuItem asChild>
				<LangSelector />
			</DropdownMenuItem>

			<DropdownMenuSeparator className='bg-border' />

			<DropdownMenuItem onClick={handleSignOut}>
				<LogOutIcon />
				{t`Se déconnecter`}
			</DropdownMenuItem>
		</>
	)
}

export const UserMenu = () => {
	const { userQuery } = useUserContext()
	const { user } = useAuth()

	const [isOpen, setIsOpen] = useState(false)

	const firstName = userQuery.data?.first_name
	const lastName = userQuery.data?.last_name
	const email = userQuery.data?.email ?? user?.email ?? undefined

	const displayName =
		formatPersonName(firstName, lastName) || user?.displayName || email || ''

	return (
		<DropdownMenu
			open={isOpen}
			onOpenChange={setIsOpen}
		>
			<DropdownMenuTrigger className='group relative flex items-center gap-2 rounded-full border p-1 leading-none'>
				<UserAvatar />

				<div className='bg-primary/80 absolute left-1 hidden size-[32px] items-center justify-center rounded-full !stroke-[3px] text-white group-hover:flex'>
					<MenuIcon className='text-primary-foreground !stroke-[3px]' />
				</div>

				<div className='hidden min-w-0 truncate overflow-hidden text-left md:block md:w-[150px]'>
					{displayName ? (
						<div className='truncate font-bold'>{displayName}</div>
					) : userQuery.isLoading ? (
						<div className='text-muted-foreground truncate font-bold'>…</div>
					) : null}

					{email ? <p className='truncate text-sm'>{email}</p> : null}
				</div>

				<ChevronDownIcon className='mr-2' />
			</DropdownMenuTrigger>

			<DropdownMenuContent
				sideOffset={15}
				alignOffset={15}
				className={cn(
					'max-md:[&_*]:text-2xl max-md:[&_[role="menuitem"]]:p-4 max-md:[&_a]:p-4 max-md:[&_button]:h-auto max-md:[&_button]:p-4 max-md:[&_svg]:size-5',
				)}
			>
				<AuthMenuContent onSignOut={() => setIsOpen(false)} />
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
